import { getConfig } from "../lib/configMgr";
import { toJSON, RequestBuilder, LLM_REQUEST_TIMEOUT } from "./request";
import {TAXID_NONE} from "../stores/AppStatusStore";
import {fetchSearch} from "./search";
import {updateLegacyId, updateLegacyIDs, wait} from "../lib/utils";
import { ROLE_ASSISTANT, ROLE_SYSTEM, ROLE_USER } from "../llm/roles";

const { ExternalRESTAPI } = getConfig();

export const fetchEntities = (ids, dsConfig, auth, fetchParams = {}) => {
  const url = new URL(ExternalRESTAPI.GetEntities, window.location.origin);
  if (dsConfig !== null) {
    url.searchParams.set('imap', dsConfig || 0);
  }
  // Use partial flag to get null for invalid IDs instead of 400 error
  url.searchParams.set('partial', 'true');
  const req = new RequestBuilder(url, fetchParams);
  return req
    .asPOST(JSON.stringify(updateLegacyIDs(ids)))
    .withJSONContentType()
    .withAuthorization(auth)
    .fetch()
    .then(toJSON);
};

export const fetchMembers = (id, dsConfig, auth, fetchParams = {}) => {
  const url = new URL(`${ExternalRESTAPI.Members}/${updateLegacyId(id)}`, window.location.origin);
  if (dsConfig !== null) {
    url.searchParams.set('imap', dsConfig || 0)
  }
  return new RequestBuilder(url, fetchParams).withJSONContentType()
      .withAuthorization(auth)
      .fetch()
      .then(toJSON);
}

export const fetchSuggestions = (searchTerms, params = {}, auth) => {
  const {category = null, taxID = null, dsConfig = 0, compact = false} = params;
  const url = new URL(ExternalRESTAPI.GetSuggestions, window.location.origin);
  // Remove square brackets with adjacent spaces (avoid modifying SMILES strings)
  const terms = typeof searchTerms === "string"
                ? [searchTerms.replace(/( \[ ?| ?] )/g, ' ')]
                : searchTerms;
  if (dsConfig !== null) {
    url.searchParams.set('imap', dsConfig || 0)
  }
  if (category) {
    url.searchParams.set("category", category);
  }
  if (taxID && taxID !== TAXID_NONE && terms.length > 1) {
    url.searchParams.set('tax_id', taxID);
  }
  if (compact) {
    url.searchParams.set('compact', compact.toString());
  }
  const req = new RequestBuilder(url);
  const body = {terms: updateLegacyIDs(terms)};
  // For testing, set this to a desired error condition
  //const cannedResponse = TypeError("Failed to fetch");
  //const cannedResponse = new Response(new Blob([JSON.stringify({})], {type: 'application/json'}),
  //                                   {status: 500, statusText: "Not OK"});
  return (
    req
      .asPOST(JSON.stringify(body))
      .withJSONContentType()
      .withAuthorization(auth)
      .fetch() /* .fetch(0, 5000, cannedResponse */
      .then(toJSON)
  );
};

export const fetchChatCompletion = (searchParams, {messages = [], historyId = null, ...chatParams}, auth) => {
  const canonical = msg => {
    const {role, content, status = null, stopReason = null} = msg;
    return {role, content, ...(status ? {status} : {}), ...(stopReason ? {stopReason} : {})}
  }
  const cleanMessages = messages
    .filter(el => el.role === ROLE_USER || el.role === ROLE_ASSISTANT).map(el => canonical(el));
  // Only spec-allowed body fields go in the body (ids, messages, templates, search_id).
  // searchParams may contain search-context fields (sim_type, category, find_related, etc.) that
  // are valid query params but not body fields — mixing them into the body triggers Connexion 400.
  // Pass everything together so constructSearchAPIRequest (via fetchSearch) routes correctly:
  // messages → body, everything else → URL query params.
  const allParams = {...searchParams, ...chatParams, messages: cleanMessages};
  if (historyId) allParams.history_id = historyId;
  // PLEX-6261: a chat turn goes to POST /chat, its own endpoint (Tim on PLEX-6261), with
  // the same body and query parameters it takes on /search, which still accepts it.
  return fetchSearch(allParams, auth, {searchType: "chat", url: ExternalRESTAPI.Chat});
};

/**
 * POST /api/oneshot with automatic polling for async 201/202 responses.
 *
 * When the server returns 201 or 202 (async job launched), waits on the job by id,
 * GET /api/oneshot/{job_id}?timeout=N (PLEX-3977), until 200 or timeout: the server
 * holds each request until the job ends or N seconds pass, so the prompt is sent once.
 * Against a backend without that route (404), it polls the old way, re-POSTing with
 * ?job_id=<id>.
 *
 * Return type depends on options.output_format:
 *   - If output_format is set: returns the final Response object
 *   - If output_format is absent: returns the response body as text (string)
 *
 * @param {string} prompt - The prompt text
 * @param {object} auth - Auth object passed to RequestBuilder
 * @param {object} options - Query params forwarded to /api/oneshot
 * @param {object} pollOptions
 * @param {number} pollOptions.pollInterval - ms between polls (default 5000)
 * @param {number} pollOptions.maxWait - total ms before giving up (default 1800000 = 30min)
 */
// Races a single oneshot POST/poll request against LLM_REQUEST_TIMEOUT — the same
// connect/response timeout applied to other LLM operations (fetchChatCompletion's
// initial POST in LLMClient.js) — so a stalled bedrock call fails fast instead of
// hanging on the raw fetch() with no timeout at all (PLEX-6160/6161).
const raceRequestTimeout = requestPromise => Promise.race([
  requestPromise,
  new Promise((_, reject) =>
    setTimeout(() => reject(new Error("oneshot request timed out waiting for server response")), LLM_REQUEST_TIMEOUT)),
]);

// PLEX-3977: how long one wait on a oneshot job may block on the server, the most it
// allows: under AWS and proxy request timeouts. The client asks again on 202.
const ONESHOT_WAIT_S = 25;

const waitForOneshot = (jobId, auth) => {
  const url = new URL(`${ExternalRESTAPI.Oneshot}/${encodeURIComponent(jobId)}`, window.location.origin);
  url.searchParams.set('timeout', `${ONESHOT_WAIT_S}`);
  return raceRequestTimeout(new RequestBuilder(url).withAuthorization(auth).asGET().fetch());
};

export const oneshot = async (prompt, auth, options = {}, {
  pollInterval = 5000,
  maxWait = 1800000,
} = {}) => {
  const url = new URL(ExternalRESTAPI.Oneshot, window.location.origin);
  Object.entries(options).forEach(([k, v]) => {
    url.searchParams.set(k, v);
  });

  let response = await raceRequestTimeout(new RequestBuilder(url)
    .withAuthorization(auth)
    .withContentType('text/plain')
    .asPOST(prompt)
    .fetch());

  if (response.status === 201 || response.status === 202) {
    const { job_id } = await response.json();
    const deadline = Date.now() + maxWait;
    let repost = false;
    let found = false;

    while (Date.now() < deadline) {
      if (repost) {
        await wait(pollInterval);

        const pollUrl = new URL(ExternalRESTAPI.Oneshot, window.location.origin);
        Object.entries(options).forEach(([k, v]) => pollUrl.searchParams.set(k, v));
        pollUrl.searchParams.set('job_id', job_id);

        response = await raceRequestTimeout(new RequestBuilder(pollUrl)
          .withAuthorization(auth)
          .withContentType('text/plain')
          .asPOST(prompt)
          .fetch());
      } else {
        const asked = Date.now();
        try {
          response = await waitForOneshot(job_id, auth);
        } catch (error) {
          if (error?.status !== 404) throw error;
          if (found) {
            // The job was there and is gone (expired, evicted, a restart). Re-POSTing
            // would run the prompt a second time, so fail instead (Tim, on #92).
            const lost = new Error(`oneshot job ${job_id} is no longer known to the server`);
            lost.name = 'OneshotJobLostError';
            lost.status = 404;
            throw lost;
          }
          // A 404 on the first wait, right after the job was created, is a backend
          // deployed before GET /oneshot/{job_id}: poll this job the old way.
          repost = true;
          continue;
        }
        found = true;
        // The server waits before answering 202; one that answers at once (a lower cap)
        // must not be asked again in a tight loop.
        if (response.status === 202 && Date.now() - asked < 1000) {
          await wait(pollInterval);
        }
      }

      if (response.status !== 202) break;
    }

    if (response.status === 202) {
      const timeoutError = new Error(`oneshot job ${job_id} did not complete within ${maxWait / 1000}s`);
      timeoutError.name = 'OneshotTimeoutError';
      throw timeoutError;
    }
  }

  if (options?.output_format) {
    return response;
  }
  return response.text();
}


export const fetchHelp = (message, sessionID = null, auth = null) => {
  const url = new URL(ExternalRESTAPI.Help, window.location.origin);
  url.searchParams.set("message", message);
  if (sessionID) {
    url.searchParams.set("session_id", sessionID);
  }
  const req = new RequestBuilder(url);
  if (auth) {
    req.withAuthorization(auth);
  }
  return req.fetch().then(toJSON);
};
